import { useEffect, useMemo, useRef, useState } from 'react';

const isAdobePostMessageCloneError = error =>
  error?.name === 'DataCloneError' &&
  typeof error?.message === 'string' &&
  error.message.includes("Failed to execute 'postMessage'");

const normalizePageNumber = page =>
  Number.isFinite(Number(page)) ? Number(page) : null;

const PDFViewer = ({
  documentId,
  pdfData,
  currentPage,
  setCurrentPage = () => {},
  setTotalPage = () => {},
  viewSDKClientComponent,
}) => {
  const viewerConfig = useMemo(
    () => ({
      showAnnotationTools: false,
      enableFormFilling: false,
      showDownloadPDF: false,
      showPrintPDF: false,
      showZoomControl: true,
      defaultViewMode: 'FIT_WIDTH',
      enableSearchAPIs: true,
    }),
    [],
  );
  const [viewSDKClient] = useState(viewSDKClientComponent);
  const previewPromiseRef = useRef(null);
  const currentPageRef = useRef(currentPage);
  const viewerDivId = useMemo(
    () => `pdf-div-${String(documentId || 'default').replace(/[^a-zA-Z0-9_-]/g, '-')}`,
    [documentId],
  );

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    let isMounted = true;

    viewSDKClient.ready().then(() => {
      if (!isMounted) {
        return;
      }

      previewPromiseRef.current = null;

      /* Invoke file preview */
      const previewFilePromise = viewSDKClient.previewFile(
        viewerDivId,
        viewerConfig,
        pdfData,
      );
      previewPromiseRef.current = previewFilePromise;

      previewFilePromise.then(adobeViewer => {
        if (!isMounted) {
          return;
        }

        const initialPage = normalizePageNumber(currentPageRef.current);
        if (initialPage != null && viewSDKClient.shouldProgrammaticGoto(initialPage)) {
          viewSDKClient.goToViewedPage(previewFilePromise, initialPage);
        }

        adobeViewer.getAPIs().then(apis => {
          apis
            .getPDFMetadata()
            .then(result => {
              if (isMounted) {
                setTotalPage(result.numPages);
              }
            })
            .catch(error => {
              if (!isAdobePostMessageCloneError(error)) {
                console.log(error);
              }
            });
        });
      });

      viewSDKClient.registerEventsHandler(setCurrentPage);
    });

    return () => {
      isMounted = false;
      previewPromiseRef.current = null;
      // Adobe Embed + React unmount can trigger Chrome's ResizeObserver loop warning if teardown
      // happens in the same layout frame. Deferring avoids the dev overlay error screen.
      const safeCleanup = () => {
        try {
          viewSDKClient.cleanup(viewerDivId);
        } catch (error) {
          // best-effort cleanup; ignore teardown errors during navigation
        }
      };

      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => window.requestAnimationFrame(safeCleanup));
      } else {
        setTimeout(safeCleanup, 0);
      }
    };
  }, [pdfData, viewerConfig, setTotalPage, setCurrentPage, viewSDKClient, viewerDivId]);

  useEffect(() => {
    const promise = previewPromiseRef.current;
    const page = normalizePageNumber(currentPage);
    if (!promise || page == null) {
      return;
    }
    if (!viewSDKClient.shouldProgrammaticGoto(page)) {
      return;
    }
    viewSDKClient.goToViewedPage(promise, page);
  }, [currentPage, viewSDKClient]);

  return (
    <div data-testid="test-pdf-viewer" id={viewerDivId} className="full-window-div h-full" />
  );
};

export default PDFViewer;
